import type { AxisGeometry } from './axis.js'
import { AXIS } from './axis.js'

// The public API surface a consumer accesses via `options.ref`.
export interface VirtualizerController {
	scrollToIndex(index: number, align?: 'start' | 'center' | 'end' | 'nearest'): void
	get range(): { first: number; last: number }
}

export class VirtualizerControllerImpl implements VirtualizerController {
	private _range: { first: number; last: number } = { first: 0, last: 0 }
	private _scrollAncestor: Element | null = null
	private _estimateSize: (index: number) => number = () => 48
	private _axis: AxisGeometry = AXIS.vertical
	private _renderWindowAt: (scrollOffset: number) => void = () => {}

	// Set by the directive once it resolves the scroll ancestor — not called by consumers.
	_bind(
		scrollAncestor: Element,
		estimateSize: (index: number) => number,
		axis: AxisGeometry,
		renderWindowAt: (scrollOffset: number) => void,
	): void {
		this._scrollAncestor = scrollAncestor
		this._estimateSize = estimateSize
		this._axis = axis
		this._renderWindowAt = renderWindowAt
	}

	// Set by the directive on each range recompute — not called by consumers.
	_setRange(first: number, last: number): void {
		this._range = { first, last }
	}

	get range(): { first: number; last: number } {
		return this._range
	}

	scrollToIndex(index: number, align: 'start' | 'center' | 'end' | 'nearest' = 'start'): void {
		if (!this._scrollAncestor) return

		let offset = 0
		for (let i = 0; i < index; i++) {
			offset += this._estimateSize(i)
		}

		const itemSize = this._estimateSize(index)
		const viewportExtent = this._scrollAncestor[this._axis.client]

		let scrollOffset: number
		switch (align) {
			case 'center':
				scrollOffset = offset - (viewportExtent - itemSize) / 2
				break
			case 'end':
				scrollOffset = offset - viewportExtent + itemSize
				break
			case 'nearest': {
				const currentStart = this._scrollAncestor[this._axis.scroll]
				const currentEnd = currentStart + viewportExtent
				if (offset < currentStart) {
					scrollOffset = offset
				} else if (offset + itemSize > currentEnd) {
					scrollOffset = offset - viewportExtent + itemSize
				} else {
					scrollOffset = currentStart // already visible
				}
				break
			}
			default:
				scrollOffset = offset
		}

		// The destination has to be mounted before the scroll is written, or a
		// snapping scroller resolves the jump against the snap areas that exist at
		// that moment and clamps it back to the nearest mounted item.
		this._renderWindowAt(scrollOffset)
		// A bare assignment rather than scrollTo({ behavior: 'smooth' }): the
		// scroller's own `scroll-behavior` decides whether it animates, which is
		// what lets a consumer that has not asked for smooth scrolling read the
		// settled position back on the next line.
		this._scrollAncestor[this._axis.scroll] = Math.max(0, scrollOffset)
	}
}
